import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

type Line = { categoryId: string; planned: string };

/** RF-ORC-04 — três origens, e a escolha muda o resultado. */
const SOURCES = [
  {
    value: 'media',
    label: 'Média dos últimos meses',
    hint: 'Parte do que você realmente gastou. Melhor para quem nunca fez orçamento.',
  },
  {
    value: 'copia',
    label: 'Copiar o período anterior',
    hint: 'Repete os limites do orçamento anterior — o orçado, nunca o realizado.',
  },
  {
    value: 'manual',
    label: 'Definir agora, linha a linha',
    hint: 'Você digita cada limite.',
  },
];

function monthRange(offset = 1) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(first), end: iso(last), label: first.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) };
}

export function BudgetForm({
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const next = monthRange(1);

  const [name, setName] = useState(next.label.charAt(0).toUpperCase() + next.label.slice(1));
  const [entityId, setEntityId] = useState(String(reference.entities[0]?.id ?? ''));
  const [periodStart, setPeriodStart] = useState(next.start);
  const [periodEnd, setPeriodEnd] = useState(next.end);
  const [source, setSource] = useState('media');
  const [averageMonths, setAverageMonths] = useState('3');
  const [expenseLimit, setExpenseLimit] = useState('');
  const [lines, setLines] = useState<Line[]>([{ categoryId: '', planned: '' }]);

  const expenseCategories = reference.categories.filter(
    (c) => c.selectable && (c.type === 'despesa' || c.type === 'ambos'),
  );

  const save = useMutation({
    mutationFn: () =>
      api.post('/budgets', {
        name,
        entity_id: Number(entityId),
        period_type: 'mensal',
        period_start: periodStart,
        period_end: periodEnd,
        source,
        ...(source === 'media' ? { average_months: Number(averageMonths) } : {}),
        ...(expenseLimit ? { expense_limit: parseInputToApi(expenseLimit) } : {}),
        ...(source === 'manual'
          ? {
              items: lines
                .filter((l) => l.categoryId !== '' && l.planned !== '')
                .map((l) => ({
                  category_id: Number(l.categoryId),
                  planned_amount: parseInputToApi(l.planned),
                })),
            }
          : {}),
      }, { entityIds: selectedIds }),
    onSuccess: (res) => {
      const meta = (res as { meta?: { warning?: string | null; items_created?: number } })?.meta;
      if (meta?.warning) window.alert(meta.warning);
      else if (meta?.items_created === 0) {
        window.alert('O orçamento foi criado sem linhas. Adicione os limites manualmente.');
      }
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const validLines = lines.filter((l) => l.categoryId !== '' && l.planned !== '');
  const canSubmit =
    name.trim().length >= 2 &&
    entityId !== '' &&
    periodStart !== '' &&
    periodEnd >= periodStart &&
    (source !== 'manual' || validLines.length > 0);

  const totalManual = validLines.reduce((acc, l) => acc + Number(parseInputToApi(l.planned)), 0);

  return (
    <Modal title="Novo orçamento" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.values(error.errors).map((m, i) => <li key={i}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Nome" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Entidade" required>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              className="input w-full"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
          <Field label="Início" required>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
          <Field label="Fim" required>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              required
              min={periodStart}
              className="input w-full"
            />
          </Field>
        </div>

        {/* A escolha da origem muda o resultado de forma relevante. */}
        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Como definir os limites
          </span>
          <div className="space-y-1.5">
            {SOURCES.map((s) => (
              <label
                key={s.value}
                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition ${
                  source === s.value
                    ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30'
                    : 'border-slate-200 dark:border-slate-800'
                }`}
              >
                <input
                  type="radio"
                  checked={source === s.value}
                  onChange={() => setSource(s.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium">{s.label}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{s.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {source === 'media' && (
          <>
            <Field label="Meses usados na média" required>
              <select
                value={averageMonths}
                onChange={(e) => setAverageMonths(e.target.value)}
                className="input w-full"
              >
                {[1, 2, 3, 6, 12].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'mês' : 'meses'}</option>
                ))}
              </select>
            </Field>

            {Number(averageMonths) < 3 && (
              <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                Base curta: um gasto atípico distorce o limite. Com 3 meses ou mais o número fica
                mais estável.
              </p>
            )}

            <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              A média parte do que você <strong>gastou</strong>, não do que deveria gastar. Se o
              período foi ruim, o teto vai legitimar o excesso — confira linha a linha depois.
            </p>
          </>
        )}

        {source === 'manual' && (
          <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Limites por categoria
            </p>

            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="flex gap-2">
                  <select
                    value={line.categoryId}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, i) => (i === index ? { ...l, categoryId: e.target.value } : l)))
                    }
                    className="input min-w-0 flex-1"
                  >
                    <option value="">Categoria</option>
                    {expenseCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.full_path}</option>
                    ))}
                  </select>
                  <input
                    value={line.planned}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, i) =>
                          i === index ? { ...l, planned: maskMoneyInput(e.target.value) } : l))
                    }
                    inputMode="numeric"
                    placeholder="0,00"
                    className="input w-28 text-right"
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                      className="px-1.5 text-rose-500"
                      aria-label="Remover linha"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setLines((prev) => [...prev, { categoryId: '', planned: '' }])}
                className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
              >
                + Linha
              </button>
              {totalManual > 0 && (
                <span className="text-sm">
                  Total orçado: <strong>{formatMoney(totalManual.toFixed(2))}</strong>
                </span>
              )}
            </div>
          </div>
        )}

        <Field label="Teto geral de despesa" hint="opcional, além dos limites por categoria">
          <input
            value={expenseLimit}
            onChange={(e) => setExpenseLimit(maskMoneyInput(e.target.value))}
            inputMode="numeric"
            placeholder="0,00"
            className="input w-full text-right"
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Criando…' : 'Criar orçamento'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
